import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    InputNumber,
    message,
    Card,
    Row,
    Col,
    Statistic,
    Typography,
    Upload,
} from 'antd';
import {
    PlusOutlined,
    CheckOutlined,
    CloseOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { personalExpenseAPI, projectAPI } from '../../services/api';  // ⭐ expenseAPI → personalExpenseAPI
import dayjs from 'dayjs';
import { getReceiptUrl } from '../../utils/urlConfig';

const { Title } = Typography;
const { TextArea } = Input;

const ExpensesPage = () => {
    const [expenses, setExpenses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        fetchExpenses();
        fetchProjects();
    }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            // ⭐ personalExpenseAPI 사용 (파라미터 없이 호출하면 백엔드에서 자동으로 본인 경비만 필터링)
            const response = await personalExpenseAPI.getAll();
            setExpenses(response.data.expenses || []);
        } catch (error) {
            message.error('경비 목록 조회 실패');
            console.error('Fetch expenses error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await projectAPI.getAll();
            setProjects(response.data.projects || []);
        } catch (error) {
            console.error('프로젝트 목록 조회 실패', error);
        }
    };

    const handleCreate = async (values) => {
        try {
            const formData = new FormData();

            // ⭐ FormData로 전송
            formData.append('project_id', values.project_id);
            formData.append('expense_date', values.expense_date.format('YYYY-MM-DD'));
            formData.append('category', values.category);
            formData.append('vendor', values.vendor);
            formData.append('amount', values.amount);
            formData.append('purpose', values.purpose);

            // 파일 추가
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('receipt', fileList[0].originFileObj);
            }

            await personalExpenseAPI.create(formData);  // ⭐ personalExpenseAPI 사용
            message.success('경비 신청이 완료되었습니다');
            setModalVisible(false);
            form.resetFields();
            setFileList([]);
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '경비 신청 실패');
        }
    };

    const handleUpdate = async (values) => {
        try {
            const formData = new FormData();

            formData.append('project_id', values.project_id);
            formData.append('expense_date', values.expense_date.format('YYYY-MM-DD'));
            formData.append('category', values.category);
            formData.append('vendor', values.vendor);
            formData.append('amount', values.amount);
            formData.append('purpose', values.purpose);

            // 새 파일 업로드
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('receipt', fileList[0].originFileObj);
            }
            // 파일 삭제 (fileList.length === 0 이고 기존에 있었던 경우)
            else if (fileList.length === 0 && editingExpense.receipt_url) {
                formData.append('remove_receipt', 'true');
            }

            await personalExpenseAPI.update(editingExpense.id, formData);  // ⭐ personalExpenseAPI 사용
            message.success('경비가 수정되었습니다');
            setModalVisible(false);
            setEditingExpense(null);
            form.resetFields();
            setFileList([]);
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '경비 수정 실패');
        }
    };

    const handleEdit = (record) => {
        setEditingExpense(record);
        form.setFieldsValue({
            project_id: record.project_id,
            expense_date: dayjs(record.expense_date),
            category: record.category,
            vendor: record.vendor,
            amount: record.amount,
            purpose: record.purpose,
        });

        // 기존 영수증이 있으면 파일 목록에 추가
        if (record.receipt_url) {
            setFileList([
                {
                    uid: '-1',
                    name: '기존 영수증',
                    status: 'done',
                    url: getReceiptUrl(record.receipt_url),
                },
            ]);
        } else {
            setFileList([]);
        }

        setModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: '경비 삭제',
            content: '정말 삭제하시겠습니까?',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await personalExpenseAPI.delete(id);  // ⭐ personalExpenseAPI 사용
                    message.success('경비가 삭제되었습니다');
                    fetchExpenses();
                } catch (error) {
                    message.error(error.response?.data?.message || '경비 삭제 실패');
                }
            },
        });
    };

    const uploadProps = {
        fileList,
        beforeUpload(file) {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                message.error('이미지 파일만 업로드 가능합니다');
                return false;
            }
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('파일 크기는 5MB 이하여야 합니다');
                return false;
            }
            return false; // ⭐ 자동 업로드 방지 (Form submit 시 전송)
        },
        onChange(info) {
            setFileList(info.fileList);
        },
        onRemove() {
            setFileList([]);
            return true;
        },
        maxCount: 1,
        listType: 'picture',
    };

    const categoryOptions = [
        { label: '식비', value: 'meal' },
        { label: '교통비', value: 'transport' },
        { label: '숙박비', value: 'accommodation' },
        { label: '소모품', value: 'supplies' },
        { label: '통신비', value: 'communication' },
        { label: '접대비', value: 'entertainment' },
        { label: '기타', value: 'other' },
    ];

    const statusMap = {
        draft: { text: '임시저장', color: 'default' },
        pm_pending: { text: 'PM 승인 대기', color: 'orange' },
        finance_pending: { text: '재무 확인 대기', color: 'blue' },
        paid: { text: '입금 완료', color: 'green' },
        rejected: { text: '반려됨', color: 'red' },
    };

    const columns = [
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => {
                const statusInfo = statusMap[status] || {};
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
        },
        {
            title: '지출일',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (category) => {
                const cat = categoryOptions.find((c) => c.value === category);
                return cat?.label || category;
            },
        },
        {
            title: '지출처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 150,
            ellipsis: true,
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount) => `${parseInt(amount).toLocaleString()}원`,
        },
        {
            title: '사용 목적',
            dataIndex: 'purpose',
            key: 'purpose',
            ellipsis: true,
        },
        {
            title: '처리일',
            key: 'processed_date',
            width: 120,
            render: (_, record) => {
                if (record.paid_at) return dayjs(record.paid_at).format('MM-DD');
                if (record.rejected_at) return dayjs(record.rejected_at).format('MM-DD');
                return '-';
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => {
                const canEdit = record.status === 'draft' || record.status === 'pm_pending';

                return canEdit ? (
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => handleEdit(record)}
                        >
                            수정
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => handleDelete(record.id)}
                        >
                            삭제
                        </Button>
                    </Space>
                ) : (
                    <Tag color={statusMap[record.status]?.color}>
                        {statusMap[record.status]?.text}
                    </Tag>
                );
            },
        },
    ];

    const statistics = {
        total: expenses.length,
        pending: expenses.filter((e) => e.status === 'pm_pending' || e.status === 'finance_pending').length,
        paid: expenses.filter((e) => e.status === 'paid').length,
        unpaid_amount: expenses
            .filter((e) => e.status !== 'paid' && e.status !== 'rejected')
            .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>개인 경비 정산</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingExpense(null);
                        form.resetFields();
                        setFileList([]);
                        setModalVisible(true);
                    }}
                >
                    경비 신청
                </Button>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic title="전체 신청" value={statistics.total} suffix="건" />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="승인 대기"
                            value={statistics.pending}
                            suffix="건"
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="입금 완료"
                            value={statistics.paid}
                            suffix="건"
                            prefix={<CheckOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="미입금 금액"
                            value={statistics.unpaid_amount}
                            suffix="원"
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={expenses}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
            />

            <Modal
                title={editingExpense ? '경비 수정' : '경비 신청'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingExpense(null);
                    form.resetFields();
                    setFileList([]);
                }}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={editingExpense ? handleUpdate : handleCreate}>
                    <Form.Item
                        name="project_id"
                        label="프로젝트"
                        rules={[{ required: true, message: '프로젝트를 선택해주세요' }]}
                    >
                        <Select
                            placeholder="프로젝트 선택"
                            options={projects.map((p) => ({
                                label: `${p.name} (${p.code})`,
                                value: p.id,
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="expense_date"
                        label="지출일"
                        rules={[{ required: true, message: '지출일을 선택해주세요' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="경비 구분"
                        rules={[{ required: true, message: '경비 구분을 선택해주세요' }]}
                    >
                        <Select placeholder="경비 구분 선택" options={categoryOptions} />
                    </Form.Item>

                    <Form.Item
                        name="vendor"
                        label="지출처"
                        rules={[{ required: true, message: '지출처를 입력해주세요' }]}
                    >
                        <Input placeholder="예: 카카오택시, 스타벅스" />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label="금액"
                        rules={[{ required: true, message: '금액을 입력해주세요' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            addonAfter="원"
                        />
                    </Form.Item>

                    <Form.Item
                        name="purpose"
                        label="사용 목적"
                        rules={[{ required: true, message: '사용 목적을 입력해주세요' }]}
                    >
                        <TextArea rows={3} placeholder="예: 고객사 미팅을 위한 교통비" />
                    </Form.Item>

                    <Form.Item label="영수증">
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />}>
                                {editingExpense && fileList.length > 0 ? '영수증 교체' : '영수증 업로드 (선택)'}
                            </Button>
                        </Upload>
                        {editingExpense && fileList.length > 0 && (
                            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                                기존 영수증이 표시됩니다. 새 파일을 업로드하면 교체됩니다.
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setModalVisible(false);
                                setEditingExpense(null);
                                form.resetFields();
                                setFileList([]);
                            }}>
                                취소
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingExpense ? '수정' : '신청'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExpensesPage;